import React from 'react'
import PropTypes from 'prop-types'
import SearchBar from './SearchBar'
import SelectedSegments from './SelectedSegments'
import Picker from './Picker'
import { api } from 'components/api'
import { supplierCommoditiesPath, supplierPagePath } from 'components/routes/path'

class Base extends React.Component {
  constructor (props) {
    super(props)

    this.tooltipRef = React.createRef()
    this.abortController = new window.AbortController()
    this.abortReason = 'Commodity Code Picker base component fetch failed'
    this.state = {
      // activeCodeIds = array of ids of codes in hierarchy containing commodity code that is "active"
      // (highlighted, has focus) due to having been selected in search or in the selected segments display.
      // Ordered from highest to lowest level, e.g. [<segment_id>, <family_id>] or [<segment_id>, <family_id>, <class_id>]
      activeCodeIds: [],
      expandedSegmentId: null,
      selectedSegments: [],
      hasUnsavedChanges: false,
      wasJustSaved: false
    }
  }

  componentDidMount () {
    document.addEventListener('mousedown', this.handleClickOutsideTooltip)
    window.addEventListener('beforeunload', this.warnOfUnsavedChanges)
    api.get({
      url: supplierCommoditiesPath(this.props.supplierId),
      headers: {
        Accept: 'application/json'
      },
      signal: this.abortController.signal
    })
      .then((response) => {
        this.setState({
          selectedSegments: response.data
        })
      })
      .catch(({ name, config }) => {
        if (name === 'CanceledError' && config?.signal?.reason === this.abortReason) { return }
        console.error(`Error while fetching ${supplierCommoditiesPath(this.props.supplierId)}`)
      })
  }

  componentWillUnmount = () => {
    document.removeEventListener('mousedown', this.handleClickOutsideTooltip)
    window.removeEventListener('beforeunload', this.warnOfUnsavedChanges)
    this.abortController.abort(this.abortReason)
  }

  handleClickOutsideTooltip = (event) => {
    if (this.tooltipRef.current && !this.tooltipRef.current.contains(event.target)) {
      $(this.tooltipRef.current).foundation('hide')
    }
  }

  warnOfUnsavedChanges = (event) => {
    if (!this.state.hasUnsavedChanges) { return }

    event.preventDefault()
    event.returnValue = 'Are you sure? Your changes have not been saved' // Returning some value is required for Chrome
  }

  renderTitle = () => (
    <div className='content-area'>
      <div className='grid-x'>
        <div className='auto cell'>
          <h3>Select Commodities</h3>
        </div>
        {/* TODO: create guided tour */}
        {/* <div className='shrink cell'> */}
        {/*  <a href='#'>Start Guided Tour</a> */}
        {/* </div> */}
      </div>
      <p>Your commodities indicate what products and services your business provides to the government. This information is displayed on your business profile and is used to list your business in relevant search results to purchasers.</p>
    </div>
  )

  updateSelectedSegment = segment => {
    this.setState((prevState) => {
      return {
        selectedSegments: [
          ...prevState.selectedSegments.filter((selectedSegment) => segment.id !== selectedSegment.id),
          segment
        ],
        hasUnsavedChanges: true,
        wasJustSaved: false
      }
    })
  }

  removeSelectedSegment = (segment) => {
    this.setState((prevState) => {
      return {
        selectedSegments: prevState.selectedSegments.filter((selectedSegment) => selectedSegment.id !== segment.id),
        hasUnsavedChanges: true,
        wasJustSaved: false
      }
    })
  }

  expandSegment = ({ code }) => {
    this.setState({
      expandedSegmentCode: code
    })
  }

  setActiveCode = (code) => {
    const activeCodeIds = []

    if (code) {
      const { segment_id: segmentId, family_id: familyId, id } = code

      if (segmentId) {
        activeCodeIds.push(segmentId.toString())
      }

      if (familyId) {
        activeCodeIds.push(familyId.toString())
      }

      activeCodeIds.push(id.toString())
    }

    this.setState({
      activeCodeIds
    })
  }

  isActiveCode = ({ id }) => {
    const { activeCodeIds } = this.state
    return activeCodeIds[activeCodeIds.length - 1] === id
  }

  isInActiveCodeHierarchy = ({ id }) => {
    return this.state.activeCodeIds.includes(id)
  }

  handleSaveClick = () => {
    this.props.handleSave(this.state.selectedSegments.flatMap(
      (segment) => segment.families.flatMap((family) => family.commodities.flatMap(({ id }) => id))
    ))

    this.setState({
      hasUnsavedChanges: false,
      wasJustSaved: true
    })
  }

  unspscTooltipTitle = () => {
    return `
      <div class="unspsc-tooltip-body">
        <h3 class="tooltip-heading">UNSPSC Codes</h3>
        <p>
          UNSPSC (United Nations Standard Product and Services Code) is a global classification standard for products and services.
        </p>
        <p>
          You can learn more about UNSPSC <a target="_blank" href="https://en.wikipedia.org/wiki/UNSPSC">here</a>.
        </p>
      </div>
    `
  }

  render () {
    return (
      <div id='commodity-code-selection-page' className='grid-x'>
        <div className='small-12 cell title-area'>
          {this.renderTitle()}
        </div>
        <div className='small-12 cell body-area'>
          <div className='content-area'>
            <div className='grid-x grid-margin-x'>
              <div className='small-12 medium-4 cell'>
                <div className='left-sticky-content'>
                  <div className='search-bar-header'>
                    <h2>
                      Search UNSPSC Codes
                    </h2>
                    <i
                      className='far fa-question-circle unspsc-tooltip'
                      title={this.unspscTooltipTitle()}
                      role='tooltip'
                      data-tooltip
                      data-allow-html='true'
                      data-position='top'
                      data-disable-hover='true'
                      data-allow-overlap='true'
                      ref={this.tooltipRef}
                    />
                  </div>
                  <SearchBar
                    handleSearchResultClick={this.setActiveCode}
                  />
                  <SelectedSegments
                    handleRemove={this.removeSelectedSegment}
                    handleClick={this.setActiveCode}
                    selectedSegments={this.state.selectedSegments}
                  />
                </div>
              </div>
              <div className='small-12 medium-8 cell commodity-code-picker-container'>
                <Picker
                  handleClick={this.addCommodityCode}
                  setActiveCode={this.setActiveCode}
                  isActiveCode={this.isActiveCode}
                  isInActiveCodeHierarchy={this.isInActiveCodeHierarchy}
                  selectedSegments={this.state.selectedSegments}
                  updateSelectedSegment={this.updateSelectedSegment}
                  removeSelectedSegment={this.removeSelectedSegment}
                />
              </div>
            </div>
          </div>
          <div className='save-button-bar grid-x align-right align-middle'>
            {
              this.state.wasJustSaved &&
                <span className='view-supplier-profile-text'>
                  View updated commodities on your
                  <a href={supplierPagePath(this.props.supplierId, { anchor: 'commodities-list-top' })}>
                    <span className='link-text'>supplier profile</span> <i className='fal fa-arrow-right' />
                  </a>
                </span>
            }
            <button className='save-button button large' onClick={this.handleSaveClick} disabled={!this.state.hasUnsavedChanges}>
              Save UNSPSC codes
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default Base

Base.propTypes = {
  handleSave: PropTypes.func.isRequired,
  supplierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}
